import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, Card, CardContent, CardActions,
    Modal, ModalClose, ModalDialog, FormControl, FormLabel, Input,
    Select, Option, Divider, CircularProgress, Alert, Snackbar, Table, Checkbox,
    IconButton
} from '@mui/joy';
import axios from 'axios';
import {
    Upload, CheckCircle, Warning, PersonAdd, Preview,
    ChevronLeft, ChevronRight, FirstPage, LastPage
} from '@mui/icons-material';
import { workshopDomain } from '../../util/domain';

const AttendanceManagement = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvPreview, setCsvPreview] = useState(null);
    const [selectedColumnIndex, setSelectedColumnIndex] = useState(null);
    const [matchType, setMatchType] = useState('mobile'); // 'mobile' or 'email'
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [uploadResults, setUploadResults] = useState(null);
    const [markResults, setMarkResults] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // Check if user has Admin role
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }
        fetchEvents();
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            setCurrentPage(1);
            fetchStudents(1);
        }
    }, [selectedEvent, attendanceStatusFilter]);

    useEffect(() => {
        if (selectedEvent) {
            fetchStudents(currentPage);
        }
    }, [currentPage]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            showNotification('Failed to fetch events', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async (page = currentPage) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = {
                page: page,
                limit: itemsPerPage
            };

            if (attendanceStatusFilter && attendanceStatusFilter !== 'all') {
                params.attendanceStatus = attendanceStatusFilter==='absentees'?'all':attendanceStatusFilter;
                if(attendanceStatusFilter==='absentees')params.absentees='1';
            }

            const response = await axios.get(`${workshopDomain}/api/attendance/students/${selectedEvent.hash_id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            if (response.data.success) {
                setStudents(response.data.students);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                    setCurrentPage(page);
                }
            }
        } catch (error) {
            console.error('Error fetching students:', error);
            showNotification('Failed to fetch students', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setSelectedStudents([]); // Clear selections when changing pages
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            showNotification('Please select a CSV file', 'error');
            return;
        }

        setCsvFile(file);
        setCsvPreview(null);
        setSelectedColumnIndex(null);

        // Preview CSV file
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('csvFile', file);

            const response = await axios.post(
                `${workshopDomain}/api/attendance/preview-csv/${selectedEvent?.hash_id || ''}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setCsvPreview(response.data);
                setPreviewDialogOpen(true);
            } else {
                showNotification(response.data.message || 'Failed to preview CSV', 'error');
            }
        } catch (error) {
            console.error('Error previewing CSV:', error);
            showNotification(error.response?.data?.message || 'Failed to preview CSV', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadCSV = async () => {
        if (!selectedEvent || !csvFile) {
            showNotification('Please select an event and CSV file', 'warning');
            return;
        }

        if (selectedColumnIndex === null || selectedColumnIndex === undefined) {
            showNotification('Please select a column from the preview', 'warning');
            return;
        }

        if (!matchType) {
            showNotification('Please select match type (Mobile or Email)', 'warning');
            return;
        }

        try {
            setUploading(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('csvFile', csvFile);
            formData.append('columnIndex', selectedColumnIndex);
            formData.append('matchType', matchType);

            const response = await axios.post(
                `${workshopDomain}/api/attendance/upload-csv/${selectedEvent.hash_id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setUploadResults(response.data.results);
                showNotification(response.data.message, 'success');
                setCsvFile(null);
                setCsvPreview(null);
                setSelectedColumnIndex(null);
                setPreviewDialogOpen(false);
                fetchStudents(currentPage); // Refresh student list with current page
            } else {
                showNotification(response.data.message || 'Upload failed', 'error');
            }
        } catch (error) {
            console.error('Error uploading CSV:', error);
            showNotification(error.response?.data?.message || 'Failed to upload CSV', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedStudents(students.map(s => s.registration_hash_id));
        } else {
            setSelectedStudents([]);
        }
    };

    const handleSelectStudent = (registrationHashId) => {
        if (selectedStudents.includes(registrationHashId)) {
            setSelectedStudents(selectedStudents.filter(id => id !== registrationHashId));
        } else {
            setSelectedStudents([...selectedStudents, registrationHashId]);
        }
    };

    const handleMarkAttended = async () => {
        if (!selectedEvent || selectedStudents.length === 0) {
            showNotification('Please select students to mark as attended', 'warning');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${workshopDomain}/api/attendance/mark-attended`,
                { registrationHashIds: selectedStudents },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setMarkResults(response.data.results);
                showNotification(response.data.message, 'success');
                setSelectedStudents([]);
                fetchStudents(currentPage); // Refresh student list with current page
            } else {
                showNotification(response.data.message || 'Failed to mark attendance', 'error');
            }
        } catch (error) {
            console.error('Error marking attendance:', error);
            showNotification(error.response?.data?.message || 'Failed to mark attendance', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 3 }}>Attendance Management</Typography>

            {/* Event Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <FormControl sx={{ mb: 2 }}>
                        <FormLabel>Select Event</FormLabel>
                        <Select
                            value={selectedEvent?.hash_id || ''}
                            onChange={(_, newValue) => {
                                const event = events.find(e => e.hash_id === newValue);
                                setSelectedEvent(event || null);
                                setUploadResults(null);
                                setMarkResults(null);
                                setCsvFile(null);
                                setCsvPreview(null);
                                setSelectedColumnIndex(null);
                            }}
                            placeholder="Choose an event"
                        >
                            {events.map(event => (
                                <Option key={event.hash_id} value={event.hash_id}>
                                    {event.title} - {new Date(event.event_start_date).toLocaleDateString()}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                </CardContent>
            </Card>

            {selectedEvent && (
                <>
                    <Divider sx={{ my: 3 }} />

                    {/* CSV Upload Section */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography level="h3" sx={{ mb: 2 }}>Upload CSV File</Typography>
                            <Typography level="body-sm" sx={{ mb: 2 }}>
                                Upload a CSV file with mobile numbers or email addresses to mark students as attended.
                                After uploading, you'll be able to preview and select the column to use.
                            </Typography>
                            <FormControl sx={{ mb: 2 }}>
                                <Input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    disabled={uploading || loading}
                                />
                            </FormControl>

                            {csvPreview && (
                                <Alert color="info" sx={{ mb: 2 }}>
                                    <Typography level="body-sm">
                                        CSV file loaded. {csvPreview.totalRows} preview rows available.
                                        {selectedColumnIndex !== null && ` Selected column: ${csvPreview.headers[selectedColumnIndex]}`}
                                    </Typography>
                                </Alert>
                            )}

                            <Button
                                startDecorator={<Upload />}
                                onClick={handleUploadCSV}
                                loading={uploading}
                                disabled={!csvFile || selectedColumnIndex === null}
                                sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                            >
                                Upload CSV
                            </Button>

                            {uploadResults && (
                                <Box sx={{ mt: 3 }}>
                                    <Alert color={uploadResults.failed.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                                        <Typography level="body-sm">
                                            Total: {uploadResults.total} |
                                            Success: {uploadResults.success.length} |
                                            Failed: {uploadResults.failed.length}
                                        </Typography>
                                    </Alert>
                                    {uploadResults.failed.length > 0 && (
                                        <Box>
                                            <Typography level="body-sm" sx={{ mb: 1 }}>Failed Records:</Typography>
                                            {uploadResults.failed.slice(0, 10).map((fail, idx) => (
                                                <Typography key={idx} level="body-xs" sx={{ color: 'danger' }}>
                                                    {fail.value}: {fail.reason}
                                                </Typography>
                                            ))}
                                            {uploadResults.failed.length > 10 && (
                                                <Typography level="body-xs" sx={{ color: 'neutral' }}>
                                                    ... and {uploadResults.failed.length - 10} more
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* CSV Preview Dialog */}
                    <Modal open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)}>
                        <ModalDialog sx={{ maxWidth: 800, width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
                            <ModalClose />
                            <Typography level="h2">CSV Preview</Typography>

                            {csvPreview && (
                                <Box sx={{ mt: 2 }}>
                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Select Column</FormLabel>
                                        <Select
                                            value={selectedColumnIndex?.toString() || ''}
                                            onChange={(_, newValue) => {
                                                setSelectedColumnIndex(parseInt(newValue));
                                            }}
                                            placeholder="Select a column"
                                        >
                                            {csvPreview.headers.map((header, index) => (
                                                <Option key={index} value={index.toString()}>
                                                    Column {index + 1}: {header || `(Empty)`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Match Type</FormLabel>
                                        <Select
                                            value={matchType}
                                            onChange={(_, newValue) => setMatchType(newValue)}
                                        >
                                            <Option value="mobile">Mobile Number</Option>
                                            <Option value="email">Email Address</Option>
                                        </Select>
                                    </FormControl>

                                    {selectedColumnIndex !== null && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                                Preview of first 10 rows from selected column:
                                            </Typography>
                                            <Table>
                                                <thead>
                                                    <tr>
                                                        <th>Row</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {csvPreview.preview.map((row, idx) => (
                                                        <tr key={idx}>
                                                            <td>{row.row}</td>
                                                            <td>{row.allColumns[selectedColumnIndex] || '(Empty)'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                                        <Button
                                            color="neutral"
                                            onClick={() => {
                                                setPreviewDialogOpen(false);
                                                setCsvFile(null);
                                                setCsvPreview(null);
                                                setSelectedColumnIndex(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={() => setPreviewDialogOpen(false)}
                                            disabled={selectedColumnIndex === null}
                                            sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                                        >
                                            Confirm Selection
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </ModalDialog>
                    </Modal>

                    <Divider sx={{ my: 3 }} />

                    {/* Manual Selection Section */}
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                <Typography level="h3">Manual Selection</Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <FormControl sx={{ minWidth: 200 }}>
                                        <FormLabel>Filter by Attendance</FormLabel>
                                        <Select
                                            value={attendanceStatusFilter}
                                            onChange={(_, newValue) => {
                                                setAttendanceStatusFilter(newValue);
                                                setCurrentPage(1);
                                                setSelectedStudents([]);
                                            }}
                                        >
                                            <Option value="all">All Students</Option>
                                            <Option value="COMPLETED">Completed</Option>
                                            <Option value="absentees">Absent from finished event</Option>
                                            <Option value="PENDING">Pending</Option>
                                            <Option value="ABSENT">Absent</Option>
                                        </Select>
                                    </FormControl>
                                    <Button
                                        startDecorator={<PersonAdd />}
                                        onClick={handleMarkAttended}
                                        loading={loading}
                                        disabled={selectedStudents.length === 0}
                                        sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                                    >
                                        Mark as Attended ({selectedStudents.length})
                                    </Button>
                                </Box>
                            </Box>

                            {/* Pagination Controls - Top */}
                            {pagination.totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                        Showing {students.length > 0 ? ((pagination.currentPage - 1) * pagination.itemsPerPage + 1) : 0}-{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} students
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => handlePageChange(1)}
                                            disabled={!pagination.hasPrevPage || loading}
                                        >
                                            <FirstPage />
                                        </IconButton>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={!pagination.hasPrevPage || loading}
                                        >
                                            <ChevronLeft />
                                        </IconButton>
                                        <Typography level="body-sm" sx={{ mx: 1 }}>
                                            Page {pagination.currentPage} of {pagination.totalPages}
                                        </Typography>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={!pagination.hasNextPage || loading}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                            disabled={!pagination.hasNextPage || loading}
                                        >
                                            <LastPage />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}

                            {loading && students.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : students.length > 0 ? (
                                <>
                                    <Table stickyHeader>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 50 }}>
                                                    <Checkbox
                                                        checked={selectedStudents.length === students.length}
                                                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Mobile</th>
                                                <th>College</th>
                                                <th>Attendance Status</th><th>Attended workshop/webinar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map(student => (
                                                <tr key={student.registration_hash_id}>
                                                    <td>
                                                        <Checkbox
                                                            checked={selectedStudents.includes(student.registration_hash_id)}
                                                            onChange={() => handleSelectStudent(student.registration_hash_id)}
                                                        />
                                                    </td>
                                                    <td>{student.name}</td>
                                                    <td>{student.email}</td>
                                                    <td>{student.mobile}</td>
                                                    <td>{student.college_name}</td>
                                                    <td>
                                                        <Select size="sm" value={student.attendance_status} onChange={async(_,status)=>{
                                                            if(!status)return;
                                                            try{await axios.put(`${workshopDomain}/api/attendance/status/${student.registration_hash_id}`,{attendanceStatus:status},{headers:{Authorization:`Bearer ${localStorage.getItem('token')}`}});await fetchStudents(currentPage);showNotification('Attendance updated.','success');}
                                                            catch(err){showNotification(err.response?.data?.message||'Attendance correction failed.','error');}
                                                        }}>{['REGISTERED','CONFIRMED','CHECKED_IN','IN_PROGRESS','COMPLETED','NO_SHOW','PARTIAL','CANCELLED'].map(status=><Option key={status} value={status}>{status}</Option>)}</Select>
                                                    </td><td>{student.attended_workshop?'Yes':'No'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {markResults && (
                                        <Box sx={{ mt: 3 }}>
                                            <Alert color={markResults.failed.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                                                <Typography level="body-sm">
                                                    Total: {markResults.total} |
                                                    Success: {markResults.success.length} |
                                                    Failed: {markResults.failed.length}
                                                </Typography>
                                            </Alert>
                                        </Box>
                                    )}

                                    {/* Pagination Controls - Bottom */}
                                    {pagination.totalPages > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 3 }}>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                disabled={!pagination.hasPrevPage || loading}
                                            >
                                                <FirstPage />
                                            </IconButton>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                disabled={!pagination.hasPrevPage || loading}
                                            >
                                                <ChevronLeft />
                                            </IconButton>
                                            <Typography level="body-sm" sx={{ mx: 2 }}>
                                                Page {pagination.currentPage} of {pagination.totalPages}
                                            </Typography>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                disabled={!pagination.hasNextPage || loading}
                                            >
                                                <ChevronRight />
                                            </IconButton>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                disabled={!pagination.hasNextPage || loading}
                                            >
                                                <LastPage />
                                            </IconButton>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Alert color="neutral">No students found for this event.</Alert>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Notification */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    variant="filled"
                    color={notification.severity}
                    onClose={closeNotification}
                    startDecorator={
                        notification.severity === 'success' ? <CheckCircle /> :
                            notification.severity === 'error' ? <Warning /> : null
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AttendanceManagement;
